import React, { useEffect, useContext, useRef, useState }  from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Layout, RangeDatepicker, Icon, Modal, useTheme } from '@ui-kitten/components'
import { getCashboxTransactionsOnDateRange, saveCashboxTransaction, editCashboxShiftStats, getStatsForDays } from '../../global/Firebase/firebase'
import { getWorkingHoursOnDateRange } from '../../global/Firebase/workingHours'
import { UserContext } from '../../global/UserProvider'
import { format, isValid } from 'date-fns'

import * as Sentry from "@sentry/react-native"
import MoneyTextInput from '../../components/MoneyTextInput'
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose, getDailyStatID, getStoreBusinessDate } from '../../utils/timezoneFunctions'
import { constantsAtom, currencyFormatterAtom, localeDateServiceAtom, paymentMethodsDictAtom, storesDictAtom, userRoleAtom, usersDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { countSystemEndAmounts } from '../Shift/ShiftScreen'
import { TRANSACTION_TYPE } from '../Shift/CashboxTransactionScreen'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { getCashWallet, shapeShiftReportAmounts, uncountedCloseEndAmounts } from '../../utils/shiftReportAmounts'
import { parseDecimalAmount } from '../../utils/decimalInput'
import { groupCashboxShifts } from '../../utils/cashboxShifts'
import { asDate } from '../../utils/timezoneUtils'

const DAY_MS = 24 * 60 * 60 * 1000

/** A frame plus the business day after it: where a till left open past the cutoff books its close. */
const withLateCloseDay = (frame) => ({
  startDate: frame.startDate,
  endDate: new Date(frame.endDate.getTime() + DAY_MS),
})

/**
 * A frame plus the business day on EACH side. The day after holds the late closes of the range's
 * last shift; the day before holds the shift that a late close at the range's START belongs to.
 * Without it that close has no previous shift to go to, and is read as a duplicate close of the
 * range's first shift (or, on a day with no open, as a close-only shift of its own).
 */
const withNeighbourDays = (frame) => ({
  startDate: new Date(frame.startDate.getTime() - DAY_MS),
  endDate: new Date(frame.endDate.getTime() + DAY_MS),
})

/** The shift's BUSINESS day and document ids, as its card's heading. */
const shiftHeading = (shift) => {
  const date = asDate(shift.date)
  const businessDay = date ? getStoreBusinessDate(shift.store, date) : null
  const day = businessDay && isValid(businessDay) ? format(businessDay, 'd/M/y E') : '—'
  return day + ' - ' + (shift.id ?? '—') + ',  ' + (shift.openId ?? '—')
}

/** One card's data: the close's fields, the open's amount and date, a key of its own. */
const shiftCardData = (shift) => {
  const card = shift.close ? { ...shift.close } : {}
  // The open and the close each name the dailyStats doc their own amounts were booked to. They are
  // the same day by construction (utils/cashboxShifts), but an edit re-states the EDITED row's own
  // doc and instant rather than the ones the merge below leaves on `dailyStatID`/`date`.
  card.closeDailyStatID = shift.close?.dailyStatID
  card.closeDate = shift.close?.date
  card.openDailyStatID = shift.open?.dailyStatID
  if (shift.open) {
    Object.assign(card, {
      openAmount: shift.open.amount,
      openId: shift.open.id,
      date: shift.open.date,
      organizationId: shift.open.organizationId,
    })
  }
  // The store as its id string, as the rows were grouped by (a row may carry a `{ id }` lookup).
  card.store = shift.store
  // ATL-1528 — every card needs an identity of its own. `id` is the CLOSE row's, so a shift with
  // no close had none, and the close-error check compared `undefined === undefined` — true — and
  // read `.message` off a null error: a render error that killed the app.
  card.shiftKey = shift.key
  card.extraCloseCount = shift.extraCloses.length
  card.extraOpenCount = shift.extraOpens.length
  return card
}

/**
 * ATL-1528 — the app has no error boundary above its screens, so a render error anywhere in a
 * shift card killed the whole process on iOS and blanked the page on web. One card that cannot be
 * rendered now says so in place and is reported, and every other shift of the range still renders.
 * This is the backstop, not the fix: the defects that threw are fixed at their source.
 */
class ShiftCardBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error, info) {
    Sentry.captureException(error, {
      tags: { atl_1528: 'shift_card_render' },
      extra: { shiftKey: this.props.shiftKey, componentStack: info?.componentStack },
    })
  }

  render() {
    if (this.state.failed) {
      return <Text status='danger' style={{ marginVertical: 10 }}>{this.props.errorText}</Text>
    }
    return this.props.children
  }
}

/**
 * Renders the card in a component BELOW the boundary. Calling `shiftView(shift)` inline would run
 * it during the screen's own render, where a throw escapes the boundary entirely — measured: the
 * boundary caught nothing until the card moved in here.
 */
const RenderShift = ({ shift, render }) => render(shift)

export default function ShiftsStats({ navigation, route }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const usersDict = useAtomValue(usersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const paymentMethods = useAtomValue(paymentMethodsDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const [data, setData] = useState(null)
  const [store, setStore] = useState(route.params?.store || context.user.store?.id)
  const [range, setRange] = useState({})
  const [showEditShiftModal, setShowEditShiftModal] = useState(false)
  const [editAmount, setEditAmount] = useState('')
  const [editType, setEditType] = useState()
  const [editShift, setEditShift] = useState()
  const [editPaymentMethod, setEditPaymentMethod] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [closeError, setCloseError] = useState(null)
  const [loadError, setLoadError] = useState(null)
  // The newest load wins: a slower read for a store or range already left must not overwrite it.
  const loadSeq = useRef(0)
  // The store on screen and the range last loaded. A reload after a save or close reads these, not
  // its own closure: that closure is from before the save's awaits, and a store or range chosen in
  // the meantime would otherwise be overwritten by the newest load — of the one already left.
  const showing = useRef({ store, range: {} })
  showing.current.store = store
  const reloadShown = () => getShiftsByDate(showing.current.range)
  const _rangePicker = React.createRef()

  useEffect(() => {
    var d = new Date()
    d.setDate(d.getDate() - 1)
    const newRange = {
      startDate: d,
      endDate: d,
    }
    setRange(newRange)
    getShiftsByDate(newRange)
  }, [store])

  const getShiftsByDate = async (newRange) => {
    _rangePicker.current?.blur()
    setData(null)
    setLoadError(null)
    const seq = ++loadSeq.current
    const loadStore = showing.current.store
    showing.current.range = newRange
    let rows
    let shown
    try {
      // The business days chosen — the frame is autoclose-shifted, so a close after local midnight
      // but before the cutoff is inside it — plus the day either side, so that a LATE close (a till
      // left open past the cutoff books it to the next day, utils/cashboxShifts) finds its shift at
      // both ends of the range. Framed inside the try: a throw here would
      // otherwise reject the load exactly as an unhandled read did.
      shown = dateRangeWithTimezoneAndAutoclose(newRange, [loadStore])
      rows = await getCashboxTransactionsOnDateRange(db, withNeighbourDays(shown), true, loadStore)
    } catch (error) {
      // Previously an unhandled rejection: the screen stayed empty with no word of why.
      Sentry.captureException(error, { tags: { atl_1528: 'shift_report_load' } })
      if (seq === loadSeq.current) setLoadError(t('error'))
      return
    }
    if (seq !== loadSeq.current) return
    let newData
    try {
      // ATL-1528 — one shift per business day, keyed by the dailyStat its rows were booked to:
      // see utils/cashboxShifts.
      const { shifts, unplaced } = groupCashboxShifts(rows, getDailyStatID, loadStore)
      if (unplaced.length > 0) {
        Sentry.captureMessage('shift report: open/close rows whose day cannot be told', {
          tags: { atl_1528: 'unplaced_rows' },
          extra: { ids: unplaced.map((row) => row?.id) },
        })
      }
      newData = shifts
        // The days read either side of the range are there only to place late closes; their own
        // shifts are not shown.
        .filter((shift) => shift.anchor >= shown.startDate && shift.anchor <= shown.endDate)
        .reverse() // newest first, as the report has always listed them
        .map((shift) => {
        const combinedValue = shiftCardData(shift)
        // The amounts as STORED. The shaped `endAmounts` below is for display only (it folds the
        // `cash` subtotal away), and an edit writes the whole map back — so it must start from this.
        combinedValue.storedEndAmounts = combinedValue.endAmounts
        // Till first, the cash-sales subtotal folded into it, each key mapped to its payment method.
        if (combinedValue.systemEndAmounts && typeof combinedValue.systemEndAmounts === 'object') {
          Object.assign(combinedValue, shapeShiftReportAmounts(combinedValue, constants?.paymentMethods?.data, paymentMethods))
        }

        return combinedValue
        })
    } catch (error) {
      // Shaping the rows runs outside the per-card boundary: a throw here would reject the load —
      // unhandled from the effect, or reported as a failed SAVE/CLOSE after a write that landed —
      // and leave the screen empty with no word of why. Say so, as a failed read does.
      Sentry.captureException(error, { tags: { atl_1528: 'shift_report_shape' } })
      setLoadError(t('error'))
      return
    }
    setData(newData)
  }

  const editShiftStats = async () => {
    // ATL-1371 — an edited shift amount was stored through `parseInt`, so 36.5 became 36 and the
    // locale comma in '36,5' did the same. Every rejected input now SAYS so, INCLUDING the empty
    // box: `editAmount` starts as '' and is never prefilled when the modal opens, so "open it and
    // tap Save" is the likeliest action of all — and it used to be a no-op with no spinner, no
    // message and no closed modal. The first version of this guard only covered a non-empty
    // unreadable value, which is the half that almost never happens.
    const numberEditAmount = parseDecimalAmount(editAmount)
    if (numberEditAmount === null) {
      setSaveError(t('notANumberError'))
      return
    }
    if (numberEditAmount < 0) {
      // Not `amountMustBePositive`: a till counted at 0 is a real, valid amount here.
      setSaveError(t('amountMustNotBeNegative'))
      return
    }
    setSaveError(null)
    setIsLoading(true)
    try {
      // The EDITED shift's store, not the one on screen: the wallet and the dailyStats doc are its.
      const shiftStore = editShift.store
      if (!shiftStore) {
        throw new Error(`editShiftStats: shift ${editShift.shiftKey} names no store`)
      }
      const cashWallet = getCashWallet(constants?.paymentMethods?.data, shiftStore)
      // The edited ROW's own day: the open's for its amount (type 0), the close's for the counted
      // amounts (type 3). The card's merged `dailyStatID` is the close's and its `date` the open's.
      const rowDay = editType === 0
        ? { dailyStatID: editShift.openDailyStatID, date: editShift.date }
        : { dailyStatID: editShift.closeDailyStatID, date: editShift.closeDate }
      await editCashboxShiftStats(
        db,
        { ...editShift, ...rowDay, endAmounts: editShift.storedEndAmounts },
        editType,
        numberEditAmount,
        shiftStore,
        editPaymentMethod,
        cashWallet
      )
      // The range on screen: the edited shift is in it, since that is where it was tapped.
      await reloadShown()
      setShowEditShiftModal(false)
    } catch(error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
    }
    setIsLoading(false)
  }

  const closeShift = async (shift) => {
    setIsLoading(true)
    setCloseError(null)
    try {
      const shiftDate = asDate(shift.date)
      if (!shiftDate || !shift.store || !shift.openId) {
        throw new Error(`closeShift: shift ${shift.shiftKey} has no open, readable date or store`)
      }
      // The shift IS its business day's dailyStats doc (utils/cashboxShifts): the close books onto
      // it, so this is the day to frame, check and write.
      const dailyStatID = shift.shiftKey
      // Frame the range on the shift's BUSINESS day (store zone, autoclose-aware) — the same day
      // `getDailyStatID` names. The device-local calendar day of `shift.date` differs from it for
      // a shift opened before the autoclose cutoff, or when the device is not in the store's zone.
      const businessDay = getStoreBusinessDate(shift.store, shiftDate)
      const localRange = dateRangeWithTimezoneAndAutoclose({startDate: businessDay, endDate: businessDay}, [shift.store])
      // Re-read the day before writing: a close written since this screen loaded (another till)
      // means the shift IS closed. Writing a second close is what the old day-split card did — 218
      // duplicate closeShift rows in production (ATL-1531).
      const dayRows = await getCashboxTransactionsOnDateRange(db, withLateCloseDay(localRange), true, shift.store)
      const current = groupCashboxShifts(dayRows, getDailyStatID, shift.store).shifts.find((s) => s.key === dailyStatID)
      if (!current?.open) {
        throw new Error(`closeShift: the open of ${dailyStatID} is no longer there`)
      }
      if (current.close) {
        setCloseError({ shiftKey: shift.shiftKey, message: t('shift.alreadyClosed') })
        await reloadShown()
        setIsLoading(false)
        return
      }
      // The expected amounts are the SHIFT's day, not today's: `dailyStatsAtom` holds only the
      // current business day, and this screen closes past shifts (it opens on yesterday). Refuse
      // rather than book another day's till against this shift (ATL-1353).
      const dayStats = await getStatsForDays(db, localRange, shift.store, false)
      const dayStat = dayStats.find(stat => stat.id === dailyStatID)
      if (!dayStat) {
        throw new Error(`closeShift: no daily stat ${dailyStatID} for the shift being closed`)
      }
      // The day itself already records a close (one the rows above do not show — e.g. its row was
      // deleted, or it predates the rows' `dailyStatID`): writing would overwrite its closing figures.
      if (dayStat.shiftClosed) {
        setCloseError({ shiftKey: shift.shiftKey, message: t('shift.alreadyClosed') })
        await reloadShown()
        setIsLoading(false)
        return
      }
      const systemEndAmounts = countSystemEndAmounts({ [shift.store]: dayStat }, shift.store)
      // (range, userId, flat, store) — no user filter, flat list, this shift's store.
      const employeesOnShift = await getWorkingHoursOnDateRange(localRange, null, true, shift.store)
      await saveCashboxTransaction(db, {
        endAmounts: uncountedCloseEndAmounts(systemEndAmounts, getCashWallet(constants?.paymentMethods?.data, shift.store)),
        systemEndAmounts,
        // The open's own instant (a report close has always been stamped so; utils/cashboxShifts
        // reads a close at its open's instant as this backdated kind), as a JS Date, which Firestore
        // stores as a Timestamp. Never the row's raw `date`: on a Supabase-read org that is the API
        // client's revived TimestampLike (a plain object with a `toDate()`) or an ISO string, and
        // written as-is it would put a non-Timestamp `date` on the cashbox row and the dailyStats
        // doc that no date-range query matches again.
        date: shiftDate,
        dailyStatID,
        cashierUID: context.user.uid,
        type: TRANSACTION_TYPE[3],
        employeesOnShift,
        store: shift.store,
        organizationId: context.user.organizationId
      })
      await reloadShown()
    } catch (error) {
      Sentry.captureException(error)
      setCloseError({ shiftKey: shift.shiftKey, message: t('error') })
    }
    setIsLoading(false)
  }

  const shiftView = (shift) => (
    <View>
      <Text category='label' style={styles.label}>
        {shiftHeading(shift)}
      </Text>
      <Layout style={styles.tableView}>
        { Array.isArray(shift.employeesOnShift) && (
          <>
            <Text category='label'>{t('shift.employeesOnShift')}</Text>
            { shift.employeesOnShift.map((employee, empIdx) => (
              <View key={`${shift.shiftKey}-${empIdx}`} style={styles.rowItem}>
                <Text style={styles.rowItemName}>{usersDict?.[employee?.employeeID]?.name}</Text>
                <Text style={styles.rowItemAmount}>{employee?.salaryType === 'day' ? '' : `${employee?.startHour?.text ?? '—'} - ${employee?.endHour?.text ?? '—'}`}</Text>
              </View>
            ))}
          </>
        )}
        { shift.openAmount != null && (
          <>
            <Text category='label'>{t('shift.byCash')}</Text>
            <View style={styles.rowItem}>
              <View style={{flex: 9, flexDirection: 'row', alignItems: 'center'}}>
                <Text>{t('shift.openAmountFact')}</Text>
                {(userRole?.permissions?.['editShiftStats'] || context.user.role === 'admin') && (
                  <TouchableOpacity onPress={() => {
                      setEditType(0)
                      setEditShift(shift)
                      // ATL-1371 — a modal that opens must not carry the LAST edit's amount or its refusal.
                      // `editAmount` is written only by the input's onChangeText, so after one successful edit
                      // the pencil on a DIFFERENT wallet/shift opened prefilled with the previous number, and
                      // "open it and tap Save" wrote that number to the wrong place with no warning.
                      setSaveError(null)
                      setEditAmount('')
                      setShowEditShiftModal(true)
                    }} style={{marginLeft: 5, padding: 3}}
                  >
                    <Icon name='edit-outline' height={15} width={15} fill={theme['color-primary-500']} />
                  </TouchableOpacity>
                )}
              </View>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(shift.openAmount)}</Text>
            </View>
          </>
        )}
        { (shift.endAmounts != null && Object.keys(shift.endAmounts).length > 0) ? Object.keys(shift.systemEndAmounts || shift.endAmounts || {}).map((item, index) => {
            // Get payment method ID from pre-computed map or fallback to item
            const paymentMethodId = shift.paymentMethodIdMap?.[item] || item
            return (
          <View key={shift.shiftKey+item}>
            <Text category='label'>{paymentMethods?.[paymentMethodId]?.name ?? t('deleted')}</Text>
                <View style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{t('shift.systemEndAmount')}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(shift.systemEndAmounts?.[item] || 0)}</Text>
                </View>
                <View style={styles.rowItem}>
                  <View style={{flex: 9, flexDirection: 'row', alignItems: 'center'}}>
                  <Text>{t('shift.cashEndAmount')}</Text>
                  {(userRole?.permissions?.['editShiftStats'] || context.user.role === 'admin') && (
                      <TouchableOpacity onPress={() => {
                          setEditType(3)
                          setEditPaymentMethod(item)
                          setEditShift(shift)
                          // ATL-1371 — a modal that opens must not carry the LAST edit's amount or its refusal.
                          // `editAmount` is written only by the input's onChangeText, so after one successful edit
                          // the pencil on a DIFFERENT wallet/shift opened prefilled with the previous number, and
                          // "open it and tap Save" wrote that number to the wrong place with no warning.
                          setSaveError(null)
                          setEditAmount('')
                          setShowEditShiftModal(true)
                        }} style={{marginLeft: 5, padding: 3}}
                      >
                        <Icon name='edit-outline' height={15} width={15} fill={theme['color-primary-500']} />
                      </TouchableOpacity>
                    )}
                  </View>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(shift.endAmounts[item] || 0)}</Text>
                </View>
              </View>
            )
          }) : (
          shift.id != null ? (
            // Closed, with no counted amounts on the close (legacy closes). It is NOT an open shift:
            // offering «Close shift» here would book a second close for it.
            <Text status='danger' style={{marginVertical: 10}}>{t('shift.closedWithoutAmounts')}</Text>
          ) : (
          <>
            <Text status='danger' style={{marginVertical: 10}}>{t('shift.shiftNotClosed')}</Text>
            <Button disabled={isLoading} onPress={() => closeShift(shift)}>
            {t('shift.closeShiftCTA')}
            </Button>
          </>
          )
        )}
        {/* On every card, not only an unclosed one: «already closed» is set just before the reload
            that shows the shift closed, and inside the unclosed branch it was never visible. */}
        {closeError != null && closeError.shiftKey === shift.shiftKey && <Text status='danger'>{closeError.message}</Text>}
        {shift.extraOpenCount > 0 && (
          <Text status='warning' style={{marginVertical: 10}}>{t('shift.duplicateOpens', { count: shift.extraOpenCount })}</Text>
        )}
        {shift.extraCloseCount > 0 && (
          <Text status='warning' style={{marginVertical: 10}}>{t('shift.duplicateCloses', { count: shift.extraCloseCount })}</Text>
        )}
      </Layout>
    </View>
  )
  
  return (
    <ScrollView style={{padding: 20}}>
      <Text category='h2' style={styles.header}>
      {t('shift.shiftStats')}
      </Text>
      <StoreChooserHorizontal store={store} showAll={false} onChange={(newStore) => {
        setStore(newStore)
      }} />
      <View style={styles.dateFilter}>
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          range={range}
          ref={_rangePicker}
          min={new Date('2020-12-01')}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              getShiftsByDate(newRange)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              getShiftsByDate({startDate: newRange.startDate, endDate})
            } else {
              setRange(newRange)
            }
          }}
        />
      </View>

      {loadError && <Text status='danger' style={{marginVertical: 10}}>{loadError}</Text>}
      { data && data.map((shift) => (
        <ShiftCardBoundary key={shift.shiftKey} shiftKey={shift.shiftKey} errorText={t('error')}>
          <RenderShift shift={shift} render={shiftView} />
        </ShiftCardBoundary>
      )) }

      <Modal backdropStyle={styles.backdrop} visible={showEditShiftModal}
              onBackdropPress={() => setShowEditShiftModal(false)}>
        <Layout style={styles.modalContainer}>
          <Text category='label' style={styles.label}>{t('shift.amount')}</Text>
          <MoneyTextInput
            value={editAmount}
            onChangeText={text => setEditAmount(text)}
            style={styles.priceField}
            returnKeyType='done'
          />
          {saveError && <Text status='danger'>{saveError}</Text>}
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <>
              <Button style={styles.submitButton} onPress={() => {editShiftStats()}}>{t('save')}</Button>
              <Button  status='basic' onPress={() => setShowEditShiftModal(false)} style={{ marginTop: 10 }}>
                {t('cancel')}
              </Button>
            </>
          )}
        </Layout>
      </Modal>
      <View style={{height: 100}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  header: {
    marginVertical: 10,
  },
  tableView: {
    padding: 10,
    marginBottom: 10,
    borderRadius: 10,
  },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 2,
  }, 
  rowItemName: {
    flex: 9
  }, 
  rowItemAmount: {
    flex: 4,
    textAlign: 'right',
  },
  label: {
    marginTop: 10,
    marginBottom: 5,
  },
  submitButton: {
    marginTop: 15,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  modalContainer: {
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 320,
    padding: 16,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
})